import { useEffect, useState } from 'react';
import type { CaptchaConfigPublic, CaptchaEndpoint } from '@pryma/auth-shared';
import { api } from '../services/api';

interface ApiCaptchaConfigResponse {
  success: boolean;
  data: {
    provider: CaptchaConfigPublic['provider'];
    site_key: string | null;
    enabled: boolean;
    enabled_endpoints: CaptchaEndpoint[];
    score_threshold: number | null;
  };
}

function adapt(raw: ApiCaptchaConfigResponse['data']): CaptchaConfigPublic {
  return {
    provider: raw.provider,
    siteKey: raw.site_key,
    enabled: raw.enabled,
    enabledEndpoints: raw.enabled_endpoints ?? [],
    scoreThreshold: raw.score_threshold,
  };
}

export interface UseCaptchaConfigResult {
  config: CaptchaConfigPublic | null;
  isLoading: boolean;
  // True when a request to render the widget for `endpoint` should be made.
  shouldChallenge: (endpoint: CaptchaEndpoint) => boolean;
}

// Fetches the platform-level CAPTCHA config (admin portal). The response is
// public — no auth required — so we cache it for the page lifetime.
export function usePlatformCaptchaConfig(): UseCaptchaConfigResult {
  const [config, setConfig] = useState<CaptchaConfigPublic | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get<ApiCaptchaConfigResponse>('/api/auth/captcha-config', { skipAuth: true })
      .then(res => {
        if (cancelled) return;
        setConfig(adapt(res.data));
      })
      .catch(() => {
        if (cancelled) return;
        // Failure to fetch the config = treat as disabled. The backend
        // re-enforces if its own config is enabled, so any false-negative
        // here is corrected to a 422 captcha_required on submit.
        setConfig(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function shouldChallenge(endpoint: CaptchaEndpoint): boolean {
    if (!config?.enabled) return false;
    if (!config.siteKey || !config.provider) return false;
    return config.enabledEndpoints.includes(endpoint);
  }

  return { config, isLoading, shouldChallenge };
}
